import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Download, Send, CheckCircle, AlertCircle, Clock, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { ROUTE_PATHS } from '@/lib/index';
import { creditScores, disputeItems, creditHacks } from '@/lib/dashboard-data';
import { springPresets } from '@/lib/motion';

export default function CreditRepair() {
  const [selectedDisputes, setSelectedDisputes] = useState<string[]>([]);
  const [letterGenerated, setLetterGenerated] = useState(false);

  const toggleDispute = (id: string) => {
    setSelectedDisputes((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Disputable':
        return <AlertCircle className="w-4 h-4 text-accent" />;
      case 'In Dispute':
        return <Clock className="w-4 h-4 text-chart-3" />;
      case 'Removed':
        return <CheckCircle className="w-4 h-4 text-primary" />;
      case 'Verified':
        return <XCircle className="w-4 h-4 text-destructive" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Disputable':
        return 'border-accent text-accent';
      case 'In Dispute':
        return 'border-chart-3 text-chart-3';
      case 'Removed':
        return 'border-primary text-primary';
      case 'Verified':
        return 'border-destructive text-destructive';
      default:
        return '';
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'Medium':
        return 'bg-accent/10 text-accent border-accent/20';
      case 'Hard':
        return 'bg-destructive/10 text-destructive border-destructive/20';
      default:
        return '';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <Link to={ROUTE_PATHS.DASHBOARD}>
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold">Credit Repair Module</h1>
              <p className="text-muted-foreground mt-1">Dispute items, generate letters, and boost your score</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* 3 Bureau Scores */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {creditScores.map((bureau, index) => (
            <motion.div
              key={bureau.bureau}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...springPresets.gentle, delay: index * 0.1 }}
            >
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="font-semibold">{bureau.bureau}</span>
                  <Badge variant="outline" style={{ color: bureau.color, borderColor: bureau.color }}>
                    +{bureau.score - bureau.previousScore}
                  </Badge>
                </div>
                <div className="text-center mb-4">
                  <div className="text-5xl font-bold font-mono mb-2" style={{ color: bureau.color }}>
                    {bureau.score}
                  </div>
                  <div className="text-sm text-muted-foreground">from {bureau.previousScore}</div>
                </div>
                <Progress value={((bureau.score - 300) / 550) * 100} className="h-3" />
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="disputes" className="space-y-6">
          <TabsList className="flex w-full overflow-x-auto">
            <TabsTrigger value="disputes" className="flex-1 whitespace-nowrap">Disputes</TabsTrigger>
            <TabsTrigger value="letters" className="flex-1 whitespace-nowrap">Letter Factory</TabsTrigger>
            <TabsTrigger value="hacks" className="flex-1 whitespace-nowrap">Score Hacks</TabsTrigger>
            <TabsTrigger value="education" className="flex-1 whitespace-nowrap">Education</TabsTrigger>
          </TabsList>

          {/* Disputes Tab */}
          <TabsContent value="disputes" className="space-y-4">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-semibold">Disputable Items</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Select items to dispute • {selectedDisputes.length} selected
                  </p>
                </div>
                <Button disabled={selectedDisputes.length === 0}>
                  Generate Letters ({selectedDisputes.length})
                </Button>
              </div>

              <div className="space-y-3">
                {disputeItems.map((item) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className={`p-4 rounded-lg border ${
                      selectedDisputes.includes(item.id) ? 'border-primary bg-primary/5' : 'border-border'
                    } transition-all`}
                  >
                    <div className="flex items-start gap-4">
                      <Checkbox
                        checked={selectedDisputes.includes(item.id)}
                        onCheckedChange={() => toggleDispute(item.id)}
                        disabled={item.status !== 'Disputable'}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-semibold">{item.type}</span>
                          <Badge variant="outline" className={getStatusColor(item.status)}>
                            {getStatusIcon(item.status)}
                            <span className="ml-1">{item.status}</span>
                          </Badge>
                          <Badge variant="outline" className="border-destructive/30 text-destructive">
                            -{item.impact} pts
                          </Badge>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          <span className="font-medium">{item.creditor}</span> • Account: {item.account}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </Card>
          </TabsContent>

          {/* Letter Factory Tab */}
          <TabsContent value="letters" className="space-y-4">
            <Card className="p-6">
              <h3 className="text-xl font-semibold mb-4">AI Letter Factory</h3>
              <p className="text-muted-foreground mb-6">
                Generate FCRA-compliant dispute letters with one click. Our AI creates personalized letters based on your
                selected items.
              </p>

              {!letterGenerated ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Send className="w-10 h-10 text-primary" />
                  </div>
                  <h4 className="text-lg font-semibold mb-2">No Letters Generated Yet</h4>
                  <p className="text-sm text-muted-foreground mb-6">
                    Select items from the Disputes tab and click "Generate Letters"
                  </p>
                  <Button onClick={() => setLetterGenerated(true)}>Generate Sample Letter</Button>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="bg-muted/30 rounded-lg p-6 border border-border">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold">Dispute Letter - Equifax</h4>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline">
                          <Download className="w-4 h-4 mr-2" />
                          Download PDF
                        </Button>
                        <Button size="sm">
                          <Send className="w-4 h-4 mr-2" />
                          Send to Bureau
                        </Button>
                      </div>
                    </div>
                    <div className="text-sm space-y-3 text-muted-foreground font-mono">
                      <p>[Your Name]</p>
                      <p>[Your Address]</p>
                      <p>[City, State ZIP]</p>
                      <p className="pt-3">Equifax Information Services LLC</p>
                      <p>P.O. Box 740256</p>
                      <p>Atlanta, GA 30374</p>
                      <p className="pt-3">Re: Dispute of Inaccurate Information</p>
                      <p className="pt-3">Dear Sir/Madam,</p>
                      <p className="pt-3">
                        I am writing to dispute the following information in my credit file. The items I dispute are
                        inaccurate and incomplete, and I am requesting that they be removed or corrected...
                      </p>
                      <p className="pt-3 text-xs text-muted-foreground/60">[Full FCRA-compliant letter content...]</p>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Score Hacks Tab */}
          <TabsContent value="hacks" className="space-y-4">
            <div className="grid md:grid-cols-2 gap-6">
              {creditHacks.map((hack, index) => (
                <motion.div
                  key={hack.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ ...springPresets.gentle, delay: index * 0.1 }}
                >
                  <Card className="p-6 h-full">
                    <div className="flex items-start justify-between mb-4">
                      <h4 className="text-lg font-semibold">{hack.title}</h4>
                      <Badge className={getDifficultyColor(hack.difficulty)}>{hack.difficulty}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">{hack.description}</p>
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className="border-primary text-primary">
                        {hack.impact}
                      </Badge>
                      <Button size="sm" variant="ghost">
                        Learn More
                      </Button>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </TabsContent>

          {/* Education Tab */}
          <TabsContent value="education" className="space-y-4">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { title: 'Understanding Credit Utilization', duration: '5 min', category: 'Basics' },
                { title: 'How to Read Your Credit Report', duration: '8 min', category: 'Basics' },
                { title: 'FCRA Rights & Protections', duration: '10 min', category: 'Legal' },
                { title: 'Building Business Credit', duration: '12 min', category: 'Advanced' },
                { title: 'Mortgage Credit Requirements', duration: '15 min', category: 'Goals' },
                { title: 'Credit Card Approval Strategies', duration: '7 min', category: 'Tactics' },
              ].map((lesson, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ ...springPresets.gentle, delay: index * 0.1 }}
                >
                  <Card className="p-6 hover:border-primary/50 transition-all cursor-pointer group">
                    <Badge variant="outline" className="mb-3">
                      {lesson.category}
                    </Badge>
                    <h4 className="font-semibold mb-2 group-hover:text-primary transition-colors">{lesson.title}</h4>
                    <p className="text-sm text-muted-foreground">{lesson.duration} read</p>
                  </Card>
                </motion.div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
